import { Column, Entity, Index, JoinColumn, ManyToOne, Unique } from 'typeorm';
import type { CrossReferenceRejectionCategory } from '../dto/cross-reference-review.dto';
import type {
	MatchEvidenceDto,
	MatchPassId,
} from '../dto/product-matching.dto';
import { BaseEntity } from './base.entity';
import { CrossReferenceRequestLineEntity } from './cross-reference-request-line.entity';
import { ItemEntity } from './item.entity';

export type CrossReferenceMatchStatus =
	| 'pending_review'
	| 'confirmed'
	| 'rejected';

/**
 * WHY a candidate was rejected, as opposed to `rejectionReason`'s free text.
 *
 * The distinction exists for undo: reverting a confirmation must restore the
 * siblings that confirmation closed out automatically, and must NOT resurrect a
 * candidate a person rejected on its own judgement. Reading the sentinel
 * strings out of `rejectionReason` cannot tell those apart — a reviewer is free
 * to type any of them into the reason box — so the cause is recorded as its own
 * column.
 *
 * `reviewer`        — a person rejected this candidate deliberately.
 * `sibling_confirm` — closed out because another candidate on the line was confirmed.
 * `superseded`      — was itself confirmed, then demoted by a later confirmation.
 * `no_match`        — closed out because the line was marked as having no match.
 */
export type CrossReferenceMatchRejectionSource =
	| 'reviewer'
	| 'sibling_confirm'
	| 'superseded'
	| 'no_match';

/**
 * One CANDIDATE item the matching engine proposed for one request line — the
 * `client_license_match` analog, and it inherits that table's hard-won shape.
 *
 * The row is durable across re-scans: the natural key is (line, item), the
 * upsert preserves terminal human statuses, and `matchedAt` records the last
 * run that still proposed this candidate. A re-match therefore refreshes
 * scores in place instead of churning the review queue's row identities.
 */
@Index('idx_crlm_line', ['fkCrossReferenceRequestLineId'])
@Index('idx_crlm_status', ['status'])
@Index('idx_crlm_item', ['fkItemId'])
// Composite backing the review queue's "is there a better sibling for this
// line" reads. The license matchmaker learned this the expensive way: without
// (status, groupKey, score) up front, the correlated lookup scans the whole
// pending set per row and the queue screen times out once volume grows.
@Index('idx_crlm_topcand', [
	'status',
	'fkCrossReferenceRequestLineId',
	'confidence',
])
@Unique('uq_crlm_line_item', ['fkCrossReferenceRequestLineId', 'fkItemId'])
@Entity('cross_reference_line_match')
export class CrossReferenceLineMatchEntity extends BaseEntity {
	@Column('int', {
		name: 'fk_cross_reference_request_line__id',
		unsigned: true,
	})
	public fkCrossReferenceRequestLineId!: number;

	@Column('int', { name: 'fk_item__id', unsigned: true })
	public fkItemId!: number;

	// ── Scoring provenance ──────────────────────────────────────────────

	/** Which pass produced this candidate (`MatchPassId`). */
	@Column('varchar', { name: 'matchPass', length: 50 })
	public matchPass!: MatchPassId;

	/** 0–100 after every applicable cap. */
	@Column('decimal', { name: 'confidence', precision: 5, scale: 2 })
	public confidence!: string;

	/** 0–100 before contradiction / identity / description caps. */
	@Column('decimal', { name: 'rawScore', precision: 5, scale: 2 })
	public rawScore!: string;

	/** Named evidence shape the band came from, for reviewer display. */
	@Column('varchar', { name: 'shape', nullable: true, length: 64 })
	public shape!: string | null;

	/**
	 * The full per-attribute evidence block. Persisted rather than recomputed
	 * because the reviewer must see the evidence that produced THIS score, not
	 * whatever today's profile would produce.
	 */
	@Column('json', { name: 'evidence' })
	public evidence!: MatchEvidenceDto;

	/** Human-readable divergence notes, generated from the verdicts. */
	@Column('json', { name: 'divergence' })
	public divergence!: Array<string>;

	@Column('tinyint', {
		name: 'autoAssignEligible',
		width: 1,
		default: () => "'0'",
	})
	public autoAssignEligible!: boolean;

	// Denormalized item fields, copied at write time. The license matchmaker's
	// lesson: a review queue that joins out to the referenced record for its
	// display columns cannot be filtered or sorted cheaply, and goes blank the
	// moment the join is unavailable.
	@Column('varchar', { name: 'itemExternalId', nullable: true, length: 64 })
	public itemExternalId!: string | null;

	@Column('text', { name: 'itemDescription', nullable: true })
	public itemDescription!: string | null;

	// ── Review state ────────────────────────────────────────────────────

	@Column('varchar', { name: 'status', length: 32, default: 'pending_review' })
	public status!: CrossReferenceMatchStatus;

	@Column('int', { name: 'reviewedByUserId', nullable: true, unsigned: true })
	public reviewedByUserId!: number | null;

	@Column('datetime', { name: 'reviewedAt', nullable: true })
	public reviewedAt!: Date | null;

	@Column('varchar', { name: 'rejectionReason', nullable: true, length: 500 })
	public rejectionReason!: string | null;

	/**
	 * The structured reason a reviewer picked, alongside the free text.
	 *
	 * A taxonomy rather than prose because this is the half of a rejection that
	 * has to be counted: "wrong dimensions" across many lines says something a
	 * paragraph cannot be aggregated into. Null on a cascade closure and on rows
	 * written before the column existed — neither carries a stated reason.
	 */
	@Column('varchar', { name: 'rejectionCategory', nullable: true, length: 32 })
	public rejectionCategory!: CrossReferenceRejectionCategory | null;

	/**
	 * Null on rows written before this column existed — a legacy row's cause is
	 * genuinely unknown, and undo treats that ambiguity explicitly rather than
	 * guessing from the reason text.
	 */
	@Column('varchar', { name: 'rejectionSource', nullable: true, length: 32 })
	public rejectionSource!: CrossReferenceMatchRejectionSource | null;

	/**
	 * The candidate whose confirmation closed this one out, when
	 * `rejectionSource` is `sibling_confirm` or `superseded`.
	 *
	 * A plain column with no FK constraint: it points at another row of THIS
	 * table, and a self-referential constraint would force a delete order on a
	 * set of rows that are always deleted together with their line anyway. The
	 * value is provenance for undo, never a join the application depends on.
	 */
	@Column('int', {
		name: 'fk_cross_reference_line_match__id_rejectedBy',
		nullable: true,
		unsigned: true,
	})
	public fkRejectedByMatchId!: number | null;

	/**
	 * "Still proposed by the latest run" stamp (the `harvestedAt` analog). Set
	 * on insert AND on update, so a candidate whose `matchedAt` predates the
	 * request's is one the current catalog no longer supports.
	 */
	@Column('datetime', { name: 'matchedAt', nullable: true })
	public matchedAt!: Date | null;

	@ManyToOne(() => CrossReferenceRequestLineEntity, (line) => line.matches, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: 'fk_cross_reference_request_line__id', referencedColumnName: 'id' },
	])
	public fkCrossReferenceRequestLine!: CrossReferenceRequestLineEntity;

	@ManyToOne(() => ItemEntity, { onDelete: 'CASCADE', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_item__id', referencedColumnName: 'id' }])
	public fkItem!: ItemEntity;
}
